"use client";

import { createContext, useContext, useState, useEffect, useCallback, useRef, ReactNode } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { authApi } from "@/api/auth";
import type { User, LoginPayload, RegisterPayload } from "@/Types/auth/auth";

//==========================================
//  Types 
//==========================================

interface AuthContextType {
    user: User | null;
    loading: boolean;
    // mutations expose isPending so your forms can show spinners
    login: (payload: LoginPayload) => Promise<{ success: boolean }>;
    isLoggingIn: boolean;
    register: (payload: RegisterPayload) => Promise<void>;
    isRegistering: boolean;
    logout: () => Promise<void>;
    isLoggingOut: boolean;
    revokeAllSessions: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

const REFRESH_INTERVAL = 14 * 60 * 1000; // 14 minutes

//===================================================
//  Protected paths (shared with axios interceptor)
//===================================================

const PROTECTED_PATHS = [
    "/myaccount",
    "/account",
    "/profile",
    "/orders",
    "/dashboard",
    "/settings",
    "/inbox",
    "/checkout",
];

const AUTH_PAGES = ["/login", "/signup", "/register"];

//=================================
//   Provider 
//=================================

export function AuthProvider({ children }: { children: ReactNode }) {

    const [user, setUser] = useState<User | null>(null);
    const [loading, setLoading] = useState(true);
    const queryClient = useQueryClient();
    const refreshTimeoutRef = useRef<NodeJS.Timeout | null>(null);
    const initializationAttempted = useRef(false);

    //================================
    // Token refresh 
    // (kept in context — TanStack Query can't do proactive timers) 
    //=================================

    const refreshAccessToken = useCallback(async (): Promise<boolean> => {

        try {
            const response = await authApi.refresh();
            if (response.user) {
                setUser(response.user);
            }
            return true;
        } catch {
            setUser(null);
            return false;
        }

    }, []);

    const scheduleTokenRefresh = useCallback(() => {

        if (refreshTimeoutRef.current) clearTimeout(refreshTimeoutRef.current);

        refreshTimeoutRef.current = setTimeout(async () => {
            const success = await refreshAccessToken();
            if (success && user) scheduleTokenRefresh();
        }, REFRESH_INTERVAL);

    }, [refreshAccessToken, user]);

    //=========================================
    //   Init: fetch /auth/me on mount 
    //=========================================

    useEffect(() => {
        if (initializationAttempted.current) return;
        initializationAttempted.current = true;

        const initAuth = async () => {
            try {
                const path = window.location.pathname;
                if (AUTH_PAGES.includes(path)) {
                    setLoading(false);
                    return;
                }

                const userData = await authApi.getMe();
                // console.log("[AuthContext] getMe response:", userData )
                setUser(userData);
            } catch(err) {
                // guest — normal, no action needed
                console.error("[AuthContext] getMe response error:", err )
            } finally {
                setLoading(false);
            }
        };

        initAuth();

        return () => {
            if (refreshTimeoutRef.current) clearTimeout(refreshTimeoutRef.current);
        };

    }, []);

    //==================================================
    //  Start/stop refresh schedule when user changes 
    //==================================================

    useEffect(() => {
        if (user) {
            scheduleTokenRefresh();
        } else {
            if (refreshTimeoutRef.current) {
                clearTimeout(refreshTimeoutRef.current);
                refreshTimeoutRef.current = null;
            }
        }

        return () => {
            if (refreshTimeoutRef.current) clearTimeout(refreshTimeoutRef.current);
        };

    }, [user, scheduleTokenRefresh]);

    //===================================
    //  Login mutation
    //===================================

    const loginMutation = useMutation({
        mutationFn: (payload: LoginPayload) => authApi.login(payload),
        onSuccess: (data) => {
            setUser(data.user);

            // Invalidate any cached queries that depend on auth state
            queryClient.invalidateQueries({ queryKey: ["cart"] });
            queryClient.invalidateQueries({ queryKey: ["wishlist"] });

            // Notify other contexts (cart/wishlist merge)
            window.dispatchEvent(
                new CustomEvent("userLogin", {
                    detail: {
                        timestamp: Date.now(),
                        cartMerged: data.cartMerged,
                        wishlistMerged: data.wishlistMerged,
                    },
                })
            );
        },
    });

    const login = async (payload: LoginPayload): Promise<{ success: boolean }> => {
        await loginMutation.mutateAsync(payload);
        return { success: true };
    };

    //===================================
    //     Register mutation
    //===================================

    const registerMutation = useMutation({
        mutationFn: (payload: RegisterPayload) => authApi.register(payload),
        onSuccess: (data) => {
            setUser(data.user);
        },
    });

    const register = async (payload: RegisterPayload): Promise<void> => {
        await registerMutation.mutateAsync(payload);
    };

    //==================================
    //      Logout mutation 
    //==================================

    const logoutMutation = useMutation({
        mutationFn: () => authApi.logout(),
        onSettled: () => {
            // Always clear user even if the request fails
            setUser(null);

            // Clear all cached queries on logout
            queryClient.clear();

            window.dispatchEvent(
                new CustomEvent("userLogout", { detail: { timestamp: Date.now() } })
            );

            // Smart redirect
            const currentPath = window.location.pathname;
            const isProtected = PROTECTED_PATHS.some((p) => currentPath.startsWith(p));

            if (isProtected) {
                window.location.href = "/";
            } else {
                window.location.reload();
            }
        },
    });

    const logout = async (): Promise<void> => {
        await logoutMutation.mutateAsync();
    };

    //===================================
    //  Revoke all sessions
    //====================================

    const revokeAllSessions = async (): Promise<void> => {
        await authApi.revokeAllSessions();
        await logout();
    };

    return (
        <AuthContext.Provider
            value={{
                user,
                loading,
                login,
                isLoggingIn: loginMutation.isPending,
                register,
                isRegistering: registerMutation.isPending,
                logout,
                isLoggingOut: logoutMutation.isPending,
                revokeAllSessions,
            }}
        >
            {children}
        </AuthContext.Provider>
    );
}

//============================
//  Hook 
//============================
export function useAuth() {
    const context = useContext(AuthContext);
    if (context === undefined) {
        throw new Error("useAuth must be used within an AuthProvider");
    }
    return context;
}